import React from 'react';
import { BhiDialog, Panel } from 'src/UIKit';
import {
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Alert,
  Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import {
  OrderStatus,
  SetupHostedNumberStep,
  SetupHostedNumberStepStatus,
  errorAlertSx,
  spreadDialogActionsSx,
  stepPanelSx,
} from 'src/modules/companies/helpers';
import {
  SetupHostedNumberDialogViewModel,
  VerificationMethod,
} from 'src/modules/companies/types';

const getStatusIcon = (status: SetupHostedNumberStepStatus, isLoading: boolean) => {
  if (isLoading) return <CircularProgress size={20} />;
  switch (status) {
    case 'completed':
      return <CheckCircleIcon color="success" />;
    case 'error':
      return <ErrorIcon color="error" />;
    case 'in-progress':
      return <CircularProgress size={20} />;
    case 'pending':
    default:
      return <PendingIcon color="disabled" />;
  }
};

const SetupHostedNumberDialogView = (props: SetupHostedNumberDialogViewModel) => {
  const {
    open,
    onClose,
    dialogTitle,
    isExistingOrderSuccessful,
    isOwnershipSuccessfulOrder,
    hasExistingOrderDocuments,
    loaUrl,
    billUrl,
    orderStatus,
    orderDescription,
    activeStep,
    loading,
    stepStatuses,
    stepErrors,
    orderId,
    verificationMethod,
    verificationCode,
    verificationCodeSent,
    setVerificationMethod,
    setVerificationCode,
    DeleteConfirmationDialog,
    onStartProcess,
    onRequestVerificationCode,
    onValidateVerificationCode,
    onRestartVerification,
    onAttachDocuments,
    onRequestDeleteOrder,
  } = props;

  const renderStepContent = (step: SetupHostedNumberStep) => {
    if (activeStep !== step) return null;

    switch (step) {
      case SetupHostedNumberStep.VERIFICATION:
        if (!verificationCodeSent) {
          return (
            <Box mt={2} pl={2}>
              <Typography variant="body2" gutterBottom>
                Select verification method:
              </Typography>
              <FormControl component="fieldset" sx={{ mt: 1 }}>
                <RadioGroup
                  value={verificationMethod}
                  onChange={e =>
                    setVerificationMethod(e.target.value as VerificationMethod)
                  }
                >
                  <FormControlLabel value="sms" control={<Radio />} label="SMS" />
                  <FormControlLabel value="call" control={<Radio />} label="Call" />
                  <FormControlLabel value="flashcall" control={<Radio />} label="Flash Call" />
                  <FormControlLabel value="manual" control={<Radio />} label="Manual" />
                </RadioGroup>
              </FormControl>
              <Box mt={2}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={onRequestVerificationCode}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : 'Request Code'}
                </Button>
              </Box>
            </Box>
          );
        }
        return (
          <Box mt={2} pl={2}>
            <Typography variant="body2" gutterBottom>
              Enter the verification code sent via {verificationMethod}:
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={e => setVerificationCode(e.target.value)}
              margin="normal"
              sx={{ maxWidth: 300 }}
            />
            <Box mt={2}>
              {stepStatuses[SetupHostedNumberStep.VERIFICATION] === 'error' &&
                stepErrors[SetupHostedNumberStep.VERIFICATION] && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={onRestartVerification}
                    disabled={loading}
                    sx={{ mr: 3 }}
                  >
                    {loading ? <CircularProgress size={20} /> : 'Try another way'}
                  </Button>
                )}
              <Button
                size="small"
                variant="contained"
                onClick={onValidateVerificationCode}
                disabled={!orderId || !verificationCode || loading}
              >
                {loading ? <CircularProgress size={20} /> : 'Validate'}
              </Button>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  const renderContent = () => {
    return (
      <Box>
        {/* Step 1: Check Eligibility */}
        <Panel sx={stepPanelSx}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box>
              {getStatusIcon(
                stepStatuses[SetupHostedNumberStep.CHECK_ELIGIBILITY],
                activeStep === SetupHostedNumberStep.CHECK_ELIGIBILITY && loading,
              )}
            </Box>
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                1. Check Eligibility
              </Typography>
              {stepStatuses[SetupHostedNumberStep.CHECK_ELIGIBILITY] === 'error' &&
                stepErrors[SetupHostedNumberStep.CHECK_ELIGIBILITY] && (
                  <Alert severity="error" sx={errorAlertSx}>
                    {stepErrors[SetupHostedNumberStep.CHECK_ELIGIBILITY]}
                  </Alert>
                )}
            </Box>
          </Box>
        </Panel>

        {/* Step 2: Create Order */}
        <Panel sx={stepPanelSx}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box>
              {getStatusIcon(
                stepStatuses[SetupHostedNumberStep.CREATE_ORDER],
                activeStep === SetupHostedNumberStep.CREATE_ORDER && loading,
              )}
            </Box>
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                2. Create Order
              </Typography>

              {stepStatuses[SetupHostedNumberStep.CREATE_ORDER] === 'error' &&
                stepErrors[SetupHostedNumberStep.CREATE_ORDER] && (
                  <Alert severity="error" sx={errorAlertSx}>
                    {stepErrors[SetupHostedNumberStep.CREATE_ORDER]}
                  </Alert>
                )}
            </Box>
          </Box>
        </Panel>

        {/* Step 3: Verification */}
        <Panel sx={stepPanelSx}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box>
              {getStatusIcon(
                stepStatuses[SetupHostedNumberStep.VERIFICATION],
                activeStep === SetupHostedNumberStep.VERIFICATION && loading && !verificationCodeSent,
              )}
            </Box>
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                3. Verification
              </Typography>
              {stepStatuses[SetupHostedNumberStep.VERIFICATION] === 'error' &&
                stepErrors[SetupHostedNumberStep.VERIFICATION] && (
                  <Alert severity="error" sx={errorAlertSx}>
                    {stepErrors[SetupHostedNumberStep.VERIFICATION]}
                  </Alert>
                )}
              {renderStepContent(SetupHostedNumberStep.VERIFICATION)}
            </Box>
          </Box>
        </Panel>

        {/* Step 4: Attach Documents */}
        <Panel sx={stepPanelSx}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box>
              {getStatusIcon(
                stepStatuses[SetupHostedNumberStep.ATTACH_DOCUMENTS],
                activeStep === SetupHostedNumberStep.ATTACH_DOCUMENTS && loading,
              )}
            </Box>
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                4. Attach Documents
              </Typography>
              {stepStatuses[SetupHostedNumberStep.ATTACH_DOCUMENTS] === 'error' &&
                stepErrors[SetupHostedNumberStep.ATTACH_DOCUMENTS] && (
                  <Alert severity="error" sx={errorAlertSx}>
                    {stepErrors[SetupHostedNumberStep.ATTACH_DOCUMENTS]}
                  </Alert>
                )}
              {loaUrl != null && billUrl != null && (
                <Stack direction="row" gap={3} mt={2}>
                  <Button
                    size="small"
                    onClick={() => window.open(loaUrl, '_blank')}
                    variant="contained"
                    color="primary"
                  >
                    View Uploaded LOA
                  </Button>
                  <Button
                    size="small"
                    onClick={() => window.open(billUrl, '_blank')}
                    variant="contained"
                    color="primary"
                  >
                    View Uploaded Phone Bill
                  </Button>
                </Stack>
              )}
            </Box>
          </Box>
        </Panel>

        {/* Success */}
        {activeStep === SetupHostedNumberStep.SUCCESS && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Phone number setup completed successfully!
            <br />
            <b>It may take up to several business days for the phone number to be configured.</b>
          </Alert>
        )}
      </Box>
    );
  };

  const renderDeleteOrderButton = () => {
    if (!orderId) return null;

    return (
      <Button
        size="small"
        onClick={onRequestDeleteOrder}
        variant="outlined"
        color="error"
        disabled={loading}
      >
        Delete Order
      </Button>
    );
  };

  const renderActions = () => {
    const hasError = Object.values(stepStatuses).some(status => status === 'error');
    const isComplete = activeStep === SetupHostedNumberStep.SUCCESS;

    const leftButtons = renderDeleteOrderButton();
    let rightButtons = null;

    if (activeStep === SetupHostedNumberStep.NOT_STARTED) {
      rightButtons = (
        <>
          <Button size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button size="small" onClick={onStartProcess} variant="contained" color="primary">
            Start Setup
          </Button>
        </>
      );
    } else if (
      activeStep === SetupHostedNumberStep.ATTACH_DOCUMENTS &&
      isOwnershipSuccessfulOrder &&
      !loading &&
      stepStatuses[SetupHostedNumberStep.ATTACH_DOCUMENTS] === 'in-progress'
    ) {
      rightButtons = (
        <>
          <Button size="small" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          {!hasExistingOrderDocuments && (
            <Button
              size="small"
              onClick={onAttachDocuments}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              Attach Documents
            </Button>
          )}
        </>
      );
    } else if (isComplete || hasError) {
      rightButtons = (
        <Button size="small" onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      );
    } else {
      rightButtons = (
        <Button size="small" variant="contained" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
      );
    }

    return { leftButtons, rightButtons };
  };

  return (
    <BhiDialog open={open} onClose={onClose} maxWidth="md" fullWidth title={dialogTitle}>
      <DialogContent>
        {isExistingOrderSuccessful && (
          <Alert severity="success" sx={{ mb: 2 }}>
            This phone number has already been successfully configured.
          </Alert>
        )}
        {orderStatus !== OrderStatus.pending && (
          <Alert
            severity={orderStatus === OrderStatus.failed ? 'error' : orderStatus === OrderStatus.successful ? 'success' : 'info'}
            sx={{ mb: 2, whiteSpace: 'pre-line', display: 'flex', alignItems: 'center', gap: 1 }}
            icon={
              orderStatus === OrderStatus.failed ? <ErrorIcon /> :
              orderStatus === OrderStatus.successful ? <CheckCircleIcon /> :
              <></>
            }
          >
            {orderDescription}
          </Alert>
        )}
        {renderContent()}
      </DialogContent>
      <DialogActions sx={spreadDialogActionsSx}>
        <Box>{renderActions().leftButtons}</Box>
        <Stack direction="row" gap={1}>{renderActions().rightButtons}</Stack>
      </DialogActions>

      <DeleteConfirmationDialog />
    </BhiDialog>
  );
};

export default SetupHostedNumberDialogView;
